/**
 * Meta Conversions API — standalone top-level page.
 *
 * Account-level CAPI settings (default access token + test event code)
 * apply to every landing page UNLESS that page overrides them in its
 * own Analytics tab. Splitting this out of Profile Settings makes the
 * "is CAPI configured?" answer one click away and surfaces the
 * Test Events workflow as a first-class operation.
 *
 * The actual config UI lives in `MetaCapiSettings` (mode="account")
 * — we just wrap it in a page-level header + context blurb so the
 * operator understands what "account-level default" means before
 * pasting an EAA... token.
 */

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserLayout } from '../components/User/UserLayout';
import { AccountCapiTokens } from '../components/Common/AccountCapiTokens';
import { Radio, ShieldCheck, Layers, ExternalLink, Lock } from 'lucide-react';
import { useEffectivePlan } from '../store/accountStore';
import { hasFeature } from '../constants/planLimits';

export const MetaCapi: React.FC = () => {
  const { t } = useTranslation('tracking');
  const navigate = useNavigate();
  const effectivePlan = useEffectivePlan();
  const hasAccess = hasFeature(effectivePlan, 'metaCapi');

  // Free / Pro tiers see a locked card instead of the live config UI.
  // We render INSIDE the UserLayout so the sidebar stays mounted —
  // dropping the user on a different layout would feel like a 404.
  // Server-side gates (auth.js + pages.js) are the real defense; this
  // is the conversion surface that turns the lock into an upgrade.
  if (!hasAccess) {
    return (
      <UserLayout>
        <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/15 via-indigo-500/15 to-purple-500/15 ring-1 ring-blue-500/20">
                <Lock className="h-7 w-7 text-blue-600" strokeWidth={2.25} />
              </div>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">{t('meta.title')}</h1>
                  <span className="rounded-full bg-gradient-to-r from-purple-500 to-pink-600 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                    Agency
                  </span>
                </div>
                <p className="mb-4 text-sm text-gray-600">
                  {t('meta.lockedDesc', {
                    defaultValue:
                      'Server-to-server Meta Conversions API — bypass ad-blockers and iOS 14.5 limits with deterministic event_id dedup. Available on Agency and Enterprise plans.',
                  })}
                </p>
                <ul className="mb-5 space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('meta.info.multi.title')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('meta.info.dualFire.title')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    <span>{t('meta.info.why.title')}</span>
                  </li>
                </ul>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 transition-all"
                  >
                    {t('meta.lockedCta', {
                      defaultValue: 'Upgrade to Agency',
                    })}
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    {t('meta.lockedBack', {
                      defaultValue: 'Back to dashboard',
                    })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Page header */}
        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Radio className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('meta.title')}</h1>
              <p className="text-sm text-gray-600">{t('meta.subtitle')}</p>
            </div>
          </div>
        </header>

        {/* "How this works" strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <InfoCard
            icon={<Layers className="w-5 h-5 text-blue-600" />}
            title={t('meta.info.multi.title')}
            body={t('meta.info.multi.body')}
          />
          <InfoCard
            icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />}
            title={t('meta.info.dualFire.title')}
            body={t('meta.info.dualFire.body')}
          />
          <InfoCard
            icon={<Radio className="w-5 h-5 text-purple-600" />}
            title={t('meta.info.why.title')}
            body={t('meta.info.why.body')}
          />
        </div>

        {/* Main settings panel */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <AccountCapiTokens />
        </div>

        {/* CAPI Replay moved to Pixel Manager → tab "Pixel Sync".
            Reasoning: workflow starts with "Pixel got banned" which
            operators discover in Pixel Manager, not here. See
            client/src/pages/PixelManager.tsx. */}

        {/* GTM auto-provisioning moved to its own top-level page
            (sidebar → Google Tag Manager). Reasoning: GTM is a Google
            product, not a Meta one — keeping it under "Meta CAPI"
            killed discoverability and the tier gates didn't line up
            (CAPI is Agency, GTM auto-sync is Enterprise). See
            client/src/pages/TagManager.tsx. */}

        {/* Footer guidance */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            {t('meta.guide.title')}
          </h3>
          <ol className="text-sm text-blue-900 space-y-1.5 list-decimal list-inside">
            <li>
              <Trans
                i18nKey="meta.guide.step1Pre"
                t={t}
                defaults="Open "
              />
              <a
                href="https://business.facebook.com/events_manager2"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-700 underline hover:text-blue-900 inline-flex items-center gap-1"
              >
                {t('meta.guide.step1Link')} <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>{t('meta.guide.step2')}</li>
            <li>{t('meta.guide.step3')}</li>
            <li>{t('meta.guide.step4')}</li>
            <li>{t('meta.guide.step5')}</li>
          </ol>
          <p className="text-xs text-blue-700 mt-3">{t('meta.guide.footer')}</p>
        </div>
      </div>
    </UserLayout>
  );
};

const InfoCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1.5">{icon}<span className="text-sm font-semibold text-gray-900">{title}</span></div>
    <p className="text-xs text-gray-600 leading-relaxed">{body}</p>
  </div>
);
